import React from 'react';
import { Star } from 'lucide-react';

interface StarRatingProps {
  rating: number;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg';
  showNumber?: boolean;
  interactive?: boolean;
  onChange?: (rating: number) => void;
}

export const StarRating: React.FC<StarRatingProps> = ({
  rating,
  maxRating = 5,
  size = 'md',
  showNumber = false,
  interactive = false,
  onChange
}) => {
  const [hoverRating, setHoverRating] = React.useState<number | null>(null);

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  const handleClick = (value: number) => {
    if (interactive && onChange) {
      onChange(value);
    }
  };

  const displayRating = hoverRating !== null ? hoverRating : rating;

  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center gap-0.5">
        {[...Array(maxRating)].map((_, index) => {
          const starValue = index + 1;
          const isFilled = starValue <= displayRating;
          const isPartial = !isFilled && starValue - 0.5 <= displayRating;

          return (
            <div
              key={index}
              className={`relative ${interactive ? 'cursor-pointer' : ''}`}
              onMouseEnter={() => interactive && setHoverRating(starValue)}
              onMouseLeave={() => interactive && setHoverRating(null)}
              onClick={() => handleClick(starValue)}
            >
              {/* Background star (empty) */}
              <Star
                className={`${sizeClasses[size]} text-gray-300`}
                fill="none"
              />

              {/* Filled star (overlay) */}
              {(isFilled || isPartial) && (
                <Star
                  className={`${sizeClasses[size]} absolute top-0 left-0 text-yellow-400`}
                  fill="currentColor"
                  style={{
                    clipPath: isPartial
                      ? 'polygon(0 0, 50% 0, 50% 100%, 0 100%)'
                      : undefined
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {showNumber && (
        <span className="text-sm font-medium text-gray-700 ml-1">
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

export default StarRating;
